import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { ENV } from '../../config/env';
import type { SectionId } from '../../types/businessPlan';
import { SECTION_METADATA } from '../../types/businessPlan';

type CommentRow = {
  id: string;
  share_id: string;
  section_id: string | null;
  author_name: string | null;
  author_email: string | null;
  content: string;
  created_at: string;
};

export default function BusinessPlanSharedComments(props: { token: string }) {
  const { token } = props;
  const [loading, setLoading] = useState(false);
  const [comments, setComments] = useState<CommentRow[]>([]);
  const [content, setContent] = useState('');
  const [authorName, setAuthorName] = useState('');
  const [authorEmail, setAuthorEmail] = useState('');
  const [sectionId, setSectionId] = useState<SectionId | ''>('');

  const sectionOptions = useMemo(() => {
    return (Object.keys(SECTION_METADATA) as SectionId[])
      .filter((id) => id !== 'cover-page' && id !== 'table-of-contents')
      .map((id) => ({ id, label: SECTION_METADATA[id].title }));
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const resp = await fetch(`${ENV.API_URL}/api/business-plans/public/shares/${token}/comments`);
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt || 'Failed to load comments');
      }
      const json = await resp.json();
      setComments(Array.isArray(json?.data) ? json.data : []);
    } catch (e: any) {
      console.error('Failed to load comments:', e);
      toast.error(e?.message || 'Failed to load comments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [token]);

  const submit = async () => {
    const trimmed = content.trim();
    if (!trimmed) return;

    try {
      const resp = await fetch(`${ENV.API_URL}/api/business-plans/public/shares/${token}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content: trimmed,
          ...(sectionId ? { section_id: sectionId } : {}),
          ...(authorName.trim() ? { author_name: authorName.trim() } : {}),
          ...(authorEmail.trim() ? { author_email: authorEmail.trim() } : {}),
        }),
      });

      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt || 'Failed to post comment');
      }

      setContent('');
      toast.success('Comment posted');
      await load();
    } catch (e: any) {
      console.error('Failed to post comment:', e);
      toast.error(e?.message || 'Failed to post comment');
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      <div className="border-b border-gray-200 px-4 py-3">
        <div className="text-sm font-semibold text-gray-900">Comments</div>
        <div className="mt-1 text-xs text-gray-600">Add questions, suggestions, or feedback on specific sections.</div>
      </div>

      <div className="space-y-3 p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-gray-700">Name (optional)</label>
            <input
              value={authorName}
              onChange={(e) => setAuthorName(e.target.value)}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              placeholder="Your name"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-gray-700">Email (optional)</label>
            <input
              value={authorEmail}
              onChange={(e) => setAuthorEmail(e.target.value)}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              placeholder="you@company.com"
            />
          </div>
          <div className="md:col-span-4">
            <label className="block text-xs font-semibold text-gray-700">Section (optional)</label>
            <select
              value={sectionId}
              onChange={(e) => setSectionId(e.target.value as any)}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="">General (whole plan)</option>
              {sectionOptions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-4">
            <label className="block text-xs font-semibold text-gray-700">Comment</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              rows={3}
              placeholder="Write a comment…"
            />
          </div>
          <div className="md:col-span-4 flex justify-end">
            <button
              type="button"
              onClick={() => void submit()}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
            >
              Post comment
            </button>
          </div>
        </div>

        <div className="space-y-2">
          {loading ? <div className="text-sm text-gray-600">Loading…</div> : null}
          {!loading && comments.length === 0 ? <div className="text-sm text-gray-600">No comments yet.</div> : null}
          {comments.map((c) => {
            const sectionLabel = c.section_id ? SECTION_METADATA[c.section_id as SectionId]?.title : null;
            return (
              <div key={c.id} className="rounded-lg border border-gray-200 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-xs text-gray-500">
                    {(c.author_name || 'Anonymous') + (c.author_email ? ` (${c.author_email})` : '')}
                    {sectionLabel ? ` · ${sectionLabel}` : ''}
                  </div>
                  <div className="text-xs text-gray-400">{new Date(c.created_at).toLocaleString()}</div>
                </div>
                <div className="mt-2 whitespace-pre-wrap text-sm text-gray-800">{c.content}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
